import type { ReactNode, SyntheticEvent } from 'react'

import { InformationCircleIcon } from '@gto/icons-react/outline/information-circle'
import { QuestionMarkCircleIcon } from '@gto/icons-react/outline/question-mark-circle'
import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { colors } from '@gto/ui/theme/design-tokens'
import type { TooltipProps } from '@gto/ui/tooltip'
import { Tooltip } from '@gto/ui/tooltip'
import { Typography } from '@gto/ui/typography'

type HelpIconTooltipProps = Pick<
  TooltipProps,
  'placement' | 'className' | 'hoverable' | 'anchor'
> & {
  content: ReactNode
  /** Mobile help is enabled by default; pass false for intentionally dense layouts. */
  displayOnMobile?: boolean
  variant?: 'question' | 'info'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  iconClassName?: string
  /** Optional text rendered next to the icon as part of the tooltip trigger. */
  label?: ReactNode
  /** Accessible name for the trigger button. The English default is a fallback only (the DS
   *  precedent — see alert-close-button); callers should pass a localized, surface-specific
   *  name. Ignored with a visible `label` (name-from-content wins) and when `embedded`. */
  ariaLabel?: string
  /** Set when the tooltip sits inside a <label>/<legend>: renders the legacy non-interactive
   *  span instead of a button — a button is a labelable element (non-conforming there) and
   *  ANY trigger name folds into the labeled control's accessible name. Embedded help stays
   *  pointer-only; prefer aria-describedby help text for form fields. */
  embedded?: boolean
}

export const HelpIconTooltip = ({
  content,
  displayOnMobile = true,
  variant = 'question',
  size = 'xs',
  placement = 'right',
  className,
  hoverable,
  anchor,
  iconClassName,
  label,
  ariaLabel = 'Help',
  embedded = false,
}: HelpIconTooltipProps) => {
  const isMobile = useMediaQuery('down', 'sm')
  const show = !isMobile || displayOnMobile

  const IconSize = {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24,
  }

  const iconStyle = {
    width: IconSize[size],
    height: IconSize[size],
    minWidth: IconSize[size],
    margin: 'auto 0',
    ...(iconClassName === undefined && { color: colors.action.active }),
  }

  const icon =
    variant === 'question' ? (
      <QuestionMarkCircleIcon
        className={cn(iconClassName)}
        size="md"
        style={iconStyle}
      />
    ) : (
      <InformationCircleIcon
        className={cn(iconClassName)}
        style={iconStyle}
      />
    )

  // Mobile taps must only open the tooltip: embedded, that default would
  // activate the host label; standalone, it would move focus off the button.
  const suppressMobileTapDefault = (event: SyntheticEvent) => {
    if (isMobile) {
      event.preventDefault()
    }
  }

  const triggerContent = (
    <>
      {icon}
      {label === undefined ? null : (
        <Typography
          color="text.secondary"
          variant="body3"
        >
          {label}
        </Typography>
      )}
    </>
  )

  return (
    <>
      {show && (
        <Tooltip
          // A measure for the prose: the base Tooltip caps its mobile popup at this same
          // `max-w-80` but its desktop popup at nothing, so a paragraph rendered as one
          // ~1600px line. Capped here rather than there because the base Tooltip also
          // carries rich layouts a blanket cap would squash. Merged BEFORE `className` so
          // a caller passing structured content can widen it.
          anchor={anchor}
          className={cn('max-w-80 break-words', className)}
          content={content}
          delay={0}
          hoverable={hoverable}
          placement={placement}
        >
          {embedded ? (
            <span
              className="inline-flex items-center gap-1"
              onClick={suppressMobileTapDefault}
              onPointerDown={suppressMobileTapDefault}
            >
              {triggerContent}
            </span>
          ) : (
            /* A real button: the tooltip is often the only home of a surface's explanation,
               and a bare span could never take focus — hover-only help does not exist for
               keyboard or screen-reader users. */
            <button
              // A visible label wins name computation untouched (WCAG 2.5.3); the aria-label
              // covers the icon-only case, where the button otherwise has no name at all.
              aria-label={label === undefined ? ariaLabel : undefined}
              className="inline-flex cursor-pointer items-center gap-1"
              type="button"
              onClick={suppressMobileTapDefault}
              onPointerDown={suppressMobileTapDefault}
            >
              {triggerContent}
            </button>
          )}
        </Tooltip>
      )}
    </>
  )
}
